import React from "react";
import { Button, FormHelperText, Grid, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import AppForm from "components/Shared/AppForm";
import BaseSelect from "components/Shared/Base/BaseSelect";
import FileDropzone from "components/Shared/FileDropzone";

import { useVehicleDetailContext } from "contexts/VehicleDetailContext";
import { useAuth, useDOUploadFile } from "hooks";
import moment from "moment";

import {ENTITY_LIST_CONSTANTS } from "data/static";
import { useListListQuery } from "store/services/listService";

const INITIAL_STATE = {
  vehicleNumber: "",
  documentName: "",
  ownerName: "",
  issueDate: "",
  expiryDate: "",
  vehicleType: "",
  attachments: [],
  account: "",
  entity: "",
  vehicleId: "",
  ownerShipType: "",
};

const VehicleRegistrationForm = ({
  isBusy = false,
  vehicleRegistration = null,
  onSubmit = () => {},
  onCancel = () => {},
}) => {
  const { viewEntity } = useAuth();
  const { handleUploadFiles } = useDOUploadFile();
  const { vehicleDetail } = useVehicleDetailContext();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
      vehicleId: vehicleDetail?._id,
    },
  });

  const issueDate = watch("issueDate");
  const expiryDate = watch("expiryDate");

  const { data: vehicleTypes = [], isLoading: loadingVehicle } =
    useListListQuery({
      type: ENTITY_LIST_CONSTANTS.VEHICLE_TYPES,
      status: "Active",
    });
  const _mappedVehicleType = React.useMemo(
    () =>
      Array.isArray(vehicleTypes)
        ? vehicleTypes.map((item) => ({
            label: item?.name,
            value: item?._id,
          }))
        : [],
    [vehicleTypes]
  );

  const { data: ownershipTypes = [], isLoading: loadingOwnershipTypes } =
     useListListQuery({
      type: ENTITY_LIST_CONSTANTS.VEHICLE_OWNERSHIP_TYPES,
      status: "Active",
    });
  const _mappedOwnershipType = React.useMemo(
    () =>
      Array.isArray(ownershipTypes)
        ? ownershipTypes.map((item) => ({
            label: item?.name,
            value: item?._id,
          }))
        : [],
    [ownershipTypes]
  );

  const handleOnVehicleRegistrationSubmit = async (data) => {
    if (!vehicleDetail?._id) {
      toast.error("Vehicle ID is missing");
      console.error("Vehicle ID is missing");
      return;
    }

    if (!_mappedOwnershipType.some((opt) => opt.value === data.ownerShipType)) {
      toast.error("Invalid ownership type selected");
      console.error("Invalid ownerShipType:", data.ownerShipType);
      return;
    }

    const _vehicleRegistrationPayload = {
      ...data,
      entity: data?.entity?._id ?? viewEntity?.entity?._id,
      account: data?.account?._id ?? viewEntity?.entity?.account?._id,
      vehicleId: data?.vehicleId?._id ?? vehicleDetail?._id,
      vehicleType: data.vehicleType,
      ownerShipType: data.ownerShipType,
      issueDate: new Date(data.issueDate),
      expiryDate: new Date(data.expiryDate),
    };

    const [existingAttachments, newAttachments] = data?.attachments?.reduce(
      ([pass, fail], el) =>
        el?.publicId || el?.url ? [[...pass, el], fail] : [pass, [...fail, el]],
      [[], []]
    );

    if (newAttachments?.length) {
      try {
        const { uploadedFiles } = await handleUploadFiles(newAttachments, {
          folderName: "VEHICLE_REGISTRATION",
          entity: viewEntity?.entity?._id,
          account: viewEntity?.entity?.account?._id,
        });

        _vehicleRegistrationPayload.attachments = [
          ...existingAttachments,
          ...uploadedFiles,
        ];
      } catch (error) {
        toast.error("Failed to upload attachments");
        console.error("Attachment upload error:", error);
        return;
      }
    }

    console.log("Submitting payload:", _vehicleRegistrationPayload);
    onSubmit(_vehicleRegistrationPayload);
  };

  React.useEffect(() => {
    if (!vehicleRegistration) return;
    reset({
      ...INITIAL_STATE,
      ...vehicleRegistration,
      entity: vehicleRegistration?.entity ?? viewEntity?.entity,
      account: vehicleRegistration?.account ?? viewEntity?.entity?.account,
      vehicleId: vehicleRegistration?.vehicleId ?? vehicleDetail?._id,
      vehicleType: vehicleRegistration?.vehicleType?._id ?? vehicleRegistration?.vehicleType ?? "",
      ownerShipType: vehicleRegistration?.ownerShipType?._id ?? vehicleRegistration?.ownerShipType ?? "",
      issueDate: vehicleRegistration?.issueDate
        ? moment(vehicleRegistration.issueDate).format("YYYY-MM-DD")
        : "",
      expiryDate: vehicleRegistration?.expiryDate
        ? moment(vehicleRegistration.expiryDate).format("YYYY-MM-DD")
        : "",
    });
  }, [vehicleRegistration, reset, vehicleDetail, viewEntity?.entity]);

  return (
    <AppForm loading={isBusy || loadingVehicle || loadingOwnershipTypes}>
      <AppForm.Content>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <Controller
              name="vehicleNumber"
              control={control}
              rules={{ required: "Vehicle Number is required" }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Vehicle Number"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="documentName"
              control={control}
              rules={{ required: "Document name is required" }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Document Name"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="ownerName"
              control={control}
              rules={{ required: "Owner Name is required" }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Owner Name"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="issueDate"
              control={control}
              rules={{
                required: "Issue Date is required",
                validate: {
                  notSameAsExpiry: (value) =>
                    !value ||
                    !expiryDate ||
                    moment(value).isBefore(moment(expiryDate)) ||
                    "Issue Date cannot be the same as or after Expiry Date",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Issue Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={
                    field.value ? moment(field.value).format("YYYY-MM-DD") : ""
                  }
                  error={!!errors.issueDate}
                  helperText={errors?.issueDate?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="expiryDate"
              control={control}
              rules={{
                required: "Expiry Date is required",
                validate: {
                  afterIssueDate: (value) =>
                    !value ||
                    !issueDate ||
                    moment(value).isAfter(moment(issueDate)) ||
                    "Expiry Date must be after Issue Date",
                  notSameAsIssue: (value) =>
                    !value ||
                    !issueDate ||
                    !moment(value).isSame(moment(issueDate), "day") ||
                    "Expiry Date cannot be the same as Issue Date",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Expiry Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={
                    field.value ? moment(field.value).format("YYYY-MM-DD") : ""
                  }
                  error={!!errors.expiryDate}
                  helperText={errors?.expiryDate?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="vehicleType"
              control={control}
              rules={{
                required: "Add Vehicle Type",
                validate: (value) =>
                  _mappedVehicleType.some((opt) => opt.value === value) ||
                  "Invalid vehicle type",
              }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Vehicle Type"
                  options={_mappedVehicleType}
                  getOptionLabel={(opt) => opt.label || ""}
                  getOptionValue={(opt) => opt.value || ""}
                  error={!!errors.vehicleType}
                  helperText={errors?.vehicleType?.message}
                  loading={loadingVehicle}
                  loadingText="Loading Vehicle Types..."
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="ownerShipType"
              control={control}
              rules={{
                required: "Add Ownership Type",
                validate: (value) =>
                  _mappedOwnershipType.some((opt) => opt.value === value) ||
                  "Invalid ownership type",
              }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Ownership Type"
                  options={_mappedOwnershipType}
                  getOptionLabel={(opt) => opt.label || ""}
                  getOptionValue={(opt) => opt.value || ""}
                  error={!!errors.ownerShipType}
                  helperText={errors?.ownerShipType?.message}
                  loading={loadingOwnershipTypes}
                  loadingText="Loading Ownership Types..."
                />
              )}
            />
          </Grid>
          <Grid item md={12}>
            <FormHelperText>Attachments</FormHelperText>
            <Controller
              name="attachments"
              control={control}
              render={({ field }) => (
                <FileDropzone
                  multiple
                  maxFiles={5}
                  files={field?.value ?? []}
                  onFileChange={(files) => setValue("attachments", files)}
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button disabled={isBusy} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnVehicleRegistrationSubmit)}
          disabled={isBusy}
        >
          {vehicleRegistration ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default VehicleRegistrationForm;